// Target companies keyed by fund_id.
// Each fund sees a different pre-acquisition due-diligence target.

const _base = (o) => ({
  confidence: "directional",
  ebitda_opportunity: o.asbc_gap_estimate * 0.6,
  ...o,
});

export const TARGETS_BY_FUND = {
  "khazanah-demo": _base({
    id: "maxis",
    name: "Maxis Berhad",
    sector: "Telecommunications",
    country: "Malaysia",
    headcount: 5000,
    estimated_labour_cost: 520000000,
    asbc_gap_estimate: 93600000,
    currency: "USD",
    data_sources: ["LinkedIn", "Bursa Malaysia filings", "Glassdoor", "Job postings", "News analysis"],
    automation_risk: [
      { function: "Network Operations", risk_pct: 68, level: "high" },
      { function: "Customer Service", risk_pct: 72, level: "high" },
      { function: "IT & Systems", risk_pct: 45, level: "medium" },
      { function: "Commercial & Sales", risk_pct: 31, level: "low" },
      { function: "Finance", risk_pct: 52, level: "medium" },
      { function: "HR & People", risk_pct: 38, level: "low" },
    ],
    leadership_scores: [6, 7, 5, 7, 5, 6, 6],
    readiness_score: 58,
    talent_gaps: [
      { capability: "AI & Machine Learning", gap: "critical", detail: "No public AI leadership hires in 18 months" },
      { capability: "Digital Product Management", gap: "significant", detail: "Limited product-led roles in job postings" },
      { capability: "Data Engineering", gap: "moderate", detail: "Some hiring activity but below sector pace" },
    ],
    key_person_risk: {
      score: 6,
      flags: [
        "CEO tenure 2.1 years \u2014 below sector median of 4.2",
        "CTO role vacant per LinkedIn signal",
        "3 of 6 C-suite hired in last 18 months \u2014 transition risk",
      ],
    },
    glassdoor: {
      rating: 3.8,
      ceo_approval: 72,
      recommend_pct: 71,
      review_themes: ["Good benefits", "Slow decision-making", "Limited innovation culture"],
    },
    restructuring_velocity: 69,
    readiness_drivers: [
      "Customer Service and Network Operations at highest near-term automation risk",
      "AI leadership capability gap is the primary bottleneck",
      "CTO vacancy creates an urgent hiring signal",
    ],
  }),

  "carlyle-demo": _base({
    id: "bajaj-electricals",
    name: "Bajaj Electricals",
    sector: "Consumer Appliances",
    country: "India",
    headcount: 4500,
    estimated_labour_cost: 380000000,
    asbc_gap_estimate: 95000000,
    currency: "INR",
    data_sources: ["LinkedIn", "BSE filings", "Glassdoor", "Job postings", "News analysis"],
    automation_risk: [
      { function: "Sales & Distribution", risk_pct: 68, level: "high" },
      { function: "Customer Service", risk_pct: 61, level: "high" },
      { function: "Finance & Accounts", risk_pct: 48, level: "medium" },
      { function: "Supply Chain", risk_pct: 42, level: "medium" },
      { function: "HR & People", risk_pct: 31, level: "low" },
      { function: "R&D & Engineering", risk_pct: 22, level: "low" },
    ],
    automation_insight: "Sales & Distribution and Customer Service represent ~42% of labour cost at high automation risk",
    leadership_scores: [6, 5, 7, 6, 5, 6, 5],
    readiness_score: 62,
    talent_gaps: [
      { capability: "Digital Commerce", gap: "critical", detail: "Limited D2C capability vs sector peers" },
      { capability: "Data & Analytics", gap: "significant", detail: "No dedicated analytics leadership visible" },
      { capability: "AI/ML Engineering", gap: "moderate", detail: "Some hiring but below sector pace" },
    ],
    key_person_risk: {
      score: 7,
      flags: [
        "MD/CEO transition recently completed \u2014 institutional knowledge gap",
        "CFO role newly filled \u2014 integration period",
        "4 of 8 C-suite hired in last 24 months",
      ],
    },
    glassdoor: {
      rating: 3.6,
      ceo_approval: 68,
      recommend_pct: 64,
      review_themes: ["Strong brand legacy", "Slow transformation pace", "Hierarchical culture"],
    },
    restructuring_velocity: 52,
    readiness_drivers: [
      "Legacy distribution network faces digital-native disruption",
      "Manufacturing workforce at high automation risk in 18 months",
      "Leadership composite 4.9/10 \u2014 capability gap for transformation",
    ],
  }),

  "stakeboat-demo": _base({
    id: "economy-process-target",
    name: "Economy Process Solutions",
    sector: "Industrial Manufacturing",
    country: "India",
    headcount: 850,
    estimated_labour_cost: 68000000,
    asbc_gap_estimate: 22000000,
    currency: "INR",
    data_sources: ["LinkedIn", "MCA filings", "Glassdoor", "Job postings", "News analysis"],
    automation_risk: [
      { function: "Manufacturing Ops", risk_pct: 71, level: "high" },
      { function: "Quality Control", risk_pct: 58, level: "high" },
      { function: "Sales & BD", risk_pct: 42, level: "medium" },
      { function: "Finance", risk_pct: 38, level: "medium" },
      { function: "HR & People", risk_pct: 28, level: "low" },
    ],
    automation_insight: "Manufacturing Ops and Quality Control represent ~55% of labour cost at high risk",
    leadership_scores: [5, 7, 4, 3, 4, 5, 6],
    readiness_score: 58,
    talent_gaps: [
      { capability: "Industrial AI/IoT", gap: "critical", detail: "No automation engineering hires in 18 months" },
      { capability: "Digital Sales", gap: "significant", detail: "100% traditional field sales model" },
      { capability: "Data Engineering", gap: "moderate", detail: "No data roles visible in job postings" },
    ],
    key_person_risk: {
      score: 8,
      flags: [
        "Founder-led \u2014 single point of institutional knowledge",
        "No visible #2 leadership layer",
        "Technical expertise concentrated in founding team",
      ],
    },
    glassdoor: {
      rating: 3.4,
      ceo_approval: 71,
      recommend_pct: 67,
      review_themes: ["Good technical culture", "Limited growth paths", "Founder dependent"],
    },
    restructuring_velocity: 38,
    readiness_drivers: [
      "Manufacturing automation risk is immediate",
      "Leadership depth below sector benchmark",
      "Digital capability gap is critical",
    ],
  }),

  "claypond-demo": _base({
    id: "akasa-air-target",
    name: "Akasa Air",
    sector: "Aviation",
    country: "India",
    headcount: 5000,
    estimated_labour_cost: 420000000,
    asbc_gap_estimate: 180000000,
    currency: "INR",
    data_sources: ["LinkedIn", "DGCA filings", "Glassdoor", "Job postings", "News analysis"],
    automation_risk: [
      { function: "Ground Operations", risk_pct: 74, level: "high" },
      { function: "Customer Experience", risk_pct: 65, level: "high" },
      { function: "Finance & Revenue", risk_pct: 49, level: "medium" },
      { function: "Technology", risk_pct: 38, level: "medium" },
      { function: "HR & People", risk_pct: 29, level: "low" },
      { function: "Flight Operations", risk_pct: 18, level: "low" },
    ],
    automation_insight: "Ground Operations and Customer Experience at critical automation risk during rapid scaling phase",
    leadership_scores: [7, 4, 8, 7, 5, 7, 6],
    readiness_score: 55,
    talent_gaps: [
      { capability: "Revenue Management AI", gap: "critical", detail: "Manual yield management vs IndiGo AI systems" },
      { capability: "Ops Technology", gap: "significant", detail: "Tech hiring below IndiGo and Air India pace" },
      { capability: "Data Science", gap: "moderate", detail: "Limited analytics roles vs sector benchmark" },
    ],
    key_person_risk: {
      score: 6,
      flags: [
        "CEO Pieter Elbers \u2014 high external profile, key person dependency",
        "Rapid headcount growth \u2014 culture dilution risk",
        "Founder vision still driving operations",
      ],
    },
    glassdoor: {
      rating: 3.9,
      ceo_approval: 82,
      recommend_pct: 76,
      review_themes: ["Exciting growth story", "Operational pressure", "Strong leadership"],
    },
    restructuring_velocity: 62,
    readiness_drivers: [
      "Rapid scaling creating workforce architecture gaps",
      "Ground ops automation lagging sector",
      "Revenue AI capability critical gap",
    ],
  }),

  "pulsar-demo": _base({
    id: "asiri-hospitals-target",
    name: "Asiri Hospitals",
    sector: "Healthcare",
    country: "Sri Lanka",
    headcount: 6500,
    estimated_labour_cost: 28000000,
    asbc_gap_estimate: 8400000,
    currency: "USD",
    data_sources: ["LinkedIn", "CSE filings", "Glassdoor", "Job postings", "News analysis"],
    automation_risk: [
      { function: "Administration", risk_pct: 69, level: "high" },
      { function: "Finance & Billing", risk_pct: 61, level: "high" },
      { function: "Patient Services", risk_pct: 44, level: "medium" },
      { function: "Clinical Support", risk_pct: 38, level: "medium" },
      { function: "HR & People", risk_pct: 27, level: "low" },
      { function: "Medical Staff", risk_pct: 12, level: "low" },
    ],
    automation_insight: "Administration and Finance represent ~38% of cost at high automation risk \u2014 significant margin recovery opportunity",
    leadership_scores: [6, 7, 5, 4, 5, 6, 7],
    readiness_score: 52,
    talent_gaps: [
      { capability: "Digital Health", gap: "critical", detail: "No digital health leadership hires in 24 months" },
      { capability: "Healthcare Analytics", gap: "significant", detail: "Limited data roles vs regional peers" },
      { capability: "Telemedicine", gap: "moderate", detail: "Below regional adoption pace" },
    ],
    key_person_risk: {
      score: 5,
      flags: [
        "Strong second-tier leadership visible",
        "Group medical director tenure 8+ years",
        "Recent CFO appointment \u2014 transition period",
      ],
    },
    glassdoor: {
      rating: 4.1,
      ceo_approval: 78,
      recommend_pct: 79,
      review_themes: ["Patient-first culture", "Strong clinical standards", "Digital transformation needed"],
    },
    restructuring_velocity: 44,
    readiness_drivers: [
      "Administrative automation opportunity is immediate and high-value",
      "Digital health capability gap vs Singapore and Thai peers",
      "Leadership composite 5.7/10 \u2014 moderate transformation capacity",
    ],
  }),

  "kgoc-demo": _base({
    id: "mekong-stationery-distribution",
    name: "Mekong Stationery Distribution (Vietnam)",
    sector: "Stationery Distribution & Retail",
    country: "Vietnam",
    headcount: 180,
    estimated_labour_cost: 144000000,
    asbc_gap_estimate: 21600000,
    currency: "INR",
    data_sources: ["LinkedIn", "SEA distribution market reports", "Vietnam trade registry", "Industry news"],
    automation_risk: [
      { function: "Field Sales & Distribution", risk_pct: 58, level: "moderate" },
      { function: "Warehousing & Logistics",    risk_pct: 72, level: "high" },
      { function: "Back-office Operations",     risk_pct: 68, level: "high" },
      { function: "Customer Service",           risk_pct: 54, level: "moderate" },
    ],
    automation_insight: "Warehousing and back-office represent the largest automation headroom; field sales less exposed given relationship-driven Vietnamese market dynamics",
    leadership_scores: [5, 4, 6, 5, 6, 5, 4],
    readiness_score: 54,
    talent_gaps: [
      { capability: "Premium Brand Selling",        gap: "critical", detail: "Current team geared to mass-market; Miles premium positioning requires retraining" },
      { capability: "Modern Trade Key Accounts",    gap: "high",     detail: "Vietnam modern trade expansion ahead of team's experience base" },
      { capability: "English-language Commercial",  gap: "moderate", detail: "Regional coordination with KGOC HQ needs English proficiency in top 3 layers" },
    ],
    key_person_risk: {
      score: 6,
      flags: [
        "Founder-led organisation; founder retiring within 24 months",
        "Top 2 sales managers together own 65% of key account relationships",
        "No formal succession plan documented",
      ],
    },
    glassdoor: {
      rating: 3.4,
      ceo_approval: 71,
      recommend_pct: 58,
      review_themes: ["loyal long-tenured workforce", "limited career progression", "traditional management style"],
    },
    restructuring_velocity: 44,
    readiness_drivers: [
      "Established Vietnam distribution footprint accelerates market entry by 18+ months vs greenfield build",
      "Workforce cultural fit with KGOC family-run heritage is high",
      "Premium brand capability gap is addressable via 12-month training intervention",
    ],
  }),

  "dsgroup-demo": _base({
    id: "rajasthan-heritage-hospitality",
    name: "Rajasthan Heritage Hospitality",
    sector: "Heritage Hotels & Resorts",
    country: "India",
    headcount: 620,
    estimated_labour_cost: 312000000,
    asbc_gap_estimate: 78000000,
    currency: "INR",
    data_sources: ["Hospitality industry reports", "Rajasthan tourism registry", "Glassdoor", "Property-level filings", "LinkedIn"],
    automation_risk: [
      { function: "Front Office & Guest Services", risk_pct: 48, level: "moderate" },
      { function: "Reservations & Revenue Mgmt",   risk_pct: 72, level: "high" },
      { function: "Housekeeping Operations",       risk_pct: 38, level: "low" },
      { function: "F&B Operations",                risk_pct: 42, level: "moderate" },
      { function: "Back-office & Accounts",        risk_pct: 68, level: "high" },
    ],
    automation_insight: "Revenue management and back-office hold the largest automation headroom; guest-facing heritage experience is deliberately high-touch and should remain human-led",
    leadership_scores: [5, 4, 6, 5, 6, 5, 4],
    readiness_score: 52,
    talent_gaps: [
      { capability: "Modern Revenue Management", gap: "critical", detail: "Dynamic pricing absent across all 4 properties; flat-rate regime dominant" },
      { capability: "Digital Guest Experience",  gap: "high",     detail: "No unified guest profile system; no personalisation engine" },
      { capability: "Learning & Development",    gap: "high",     detail: "No L&D infrastructure; Raymond University-style intervention needed post-acquisition" },
      { capability: "GM Succession Bench",       gap: "critical", detail: "2 of 4 GMs at retirement age within 18 months; no internal successors identified" },
    ],
    key_person_risk: {
      score: 7,
      flags: [
        "Family-office run; absence of professional HRMS and payroll infrastructure",
        "Heritage property staff tenure >10 years average \u2014 cultural absorption risk on integration",
        "Top GM cohort retirement creates a simultaneous 2-property leadership gap",
      ],
    },
    glassdoor: {
      rating: 3.8,
      ceo_approval: 72,
      recommend_pct: 66,
      review_themes: ["loyal heritage workforce", "strong guest reputation", "limited career progression", "traditional management"],
    },
    restructuring_velocity: 38,
    readiness_drivers: [
      "Namah brand extension into heritage segment adds 4 properties in one move toward 6\u219212 target",
      "Central procurement synergy with existing 6 hotels immediate",
      "Asset-light upgrade option for 2 of 4 properties reduces integration capex",
    ],
  }),

  "chryscapital-demo": _base({
    id: "gebbs-target",
    name: "GeBBS Healthcare Solutions",
    sector: "Healthcare Revenue Cycle Management",
    country: "India/USA",
    headcount: 8000,
    estimated_labour_cost: 520000000,
    asbc_gap_estimate: 156000000,
    currency: "INR",
    data_sources: ["LinkedIn", "Glassdoor", "Job postings", "News analysis", "Company website"],
    automation_risk: [
      { function: "Medical Coding", risk_pct: 78, level: "high" },
      { function: "Claims Processing", risk_pct: 72, level: "high" },
      { function: "Patient Access", risk_pct: 55, level: "medium" },
      { function: "AR & Denials", risk_pct: 48, level: "medium" },
      { function: "Quality Audit", risk_pct: 38, level: "medium" },
      { function: "HR & People", risk_pct: 24, level: "low" },
    ],
    automation_insight: "Medical Coding and Claims Processing represent ~45% of labour cost at critical automation risk \u2014 highest AI displacement risk in healthcare RCM",
    leadership_scores: [6, 7, 5, 5, 5, 6, 6],
    readiness_score: 63,
    talent_gaps: [
      { capability: "AI-Augmented Coding", gap: "critical", detail: "No AI coding leadership hires vs Omega Healthcare and nThrive" },
      { capability: "Revenue Cycle Analytics", gap: "significant", detail: "Limited analytics roles vs US-based RCM peers" },
      { capability: "Automation Engineering", gap: "moderate", detail: "RPA adoption below sector benchmark" },
    ],
    key_person_risk: {
      score: 6,
      flags: [
        "CEO Nisheet Gupta \u2014 strong external profile",
        "US leadership team recently rebuilt post-acquisition",
        "Clinical expertise concentrated in senior layer",
      ],
    },
    glassdoor: {
      rating: 3.7,
      ceo_approval: 74,
      recommend_pct: 69,
      review_themes: ["Strong growth trajectory", "Intense work pace", "AI transformation underway"],
    },
    restructuring_velocity: 48,
    readiness_drivers: [
      "Medical coding automation is immediate \u2014 AI tools mature",
      "Competitor Omega Healthcare already deploying AI at scale",
      "Post-ChrysCapital investment is optimal window for redesign",
    ],
  }),
};

// Slug aliases so both "khazanah" and "khazanah-demo" resolve
const _SLUG_ALIAS = {
  "khazanah": "khazanah-demo",
  "carlyle": "carlyle-demo",
  "stakeboat": "stakeboat-demo",
  "claypond": "claypond-demo",
  "pulsar": "pulsar-demo",
  "chryscapital": "chryscapital-demo",
  "kgoc": "kgoc-demo",
  "dsgroup": "dsgroup-demo",
};

export function getTarget(slug) {
  const key = _SLUG_ALIAS[slug] || slug;
  return TARGETS_BY_FUND[key] || TARGETS_BY_FUND["khazanah-demo"];
}
